import { createContext, Dispatch } from 'react';
import { Agent, ChatModel, Citation, Conversation, CurrentUser, CustomGPT, Feedback, IndividualFileChat, Notification, Personalization, WeeklyUsage } from '@/api/models';

export interface AppState {
  theme: string;
  currentUser: CurrentUser | null;
  userGroups: string[];
  sidepanelOpen: boolean;
  sidepanelTransitioning: boolean;
  frontendSettings: any | null;
  notifications: Notification[] | null;
  weeklyUsage: WeeklyUsage | null;
  searchPopupOpen: boolean;
  chatHistory: Conversation[] | null;
  groupChats: Conversation[] | null;
  customGPTs: CustomGPT[] | null;
  customGPTsLoaded: boolean;
  currentModel: ChatModel | null;
  currentCustomGPT: CustomGPT | null;
  agents: Agent[] | null;
  currentAgent: Agent | null;
  chatPanelTransitioned: boolean;
  currentChat: Conversation | null;
  convFilesOpen: boolean;
  fileUpload: any;
  imageUpload: any;
  individualFileChat: IndividualFileChat[] | null;
  feedbackState: { [answerId: string]: Feedback.Neutral | Feedback.Positive | Feedback.Negative; };
  selectedCitation: Citation | null;
  selectedCitations: Citation[] | null;
  selectedConvStarter: string | null;
  selectedTool: string | null;
  personalization: Personalization;
  personalizationLoading: boolean;
  personalizationError: string | Error | null;
}

export type Action =
  | { type: 'TOGGLE_THEME' }
  | { type: 'SET_CURRENT_USER', payload: CurrentUser | null }
  | { type: 'SET_USER_GROUPS', payload: string[] }
  | { type: 'SET_SIDEPANEL', payload: boolean }
  | { type: 'SET_SIDEPANEL_TRANSITIONING', payload: boolean }
  | { type: 'LOAD_FRONTEND_SETTINGS', payload: any | null }
  | { type: 'LOAD_NOTIFICATIONS', payload: Notification[] | null }
  | { type: 'LOAD_USAGE', payload: WeeklyUsage | null }
  | { type: 'SET_SEARCH_POPUP_OPEN', payload: boolean }
  | { type: 'LOAD_CHAT_HISTORY', payload: Conversation[] | null }
  | { type: 'UPDATE_CONVERSATION_PIN', payload: Conversation }
  | { type: 'UPDATE_CONVERSATION_TITLE', payload: Conversation }
  | { type: 'DELETE_CONVERSATION', payload: string }
  | { type: 'LOAD_GROUP_CHATS', payload: Conversation[] | null }
  | { type: 'ADD_GROUP_CHAT', payload: Conversation }
  | { type: 'UPDATE_GROUP_CHAT', payload: Conversation }
  | { type: 'DELETE_GROUP_CHAT', payload: string }
  | { type: 'LOAD_CUSTOM_GPTS', payload: CustomGPT[] | null }
  | { type: 'SET_CUSTOM_GPTS_LOADED', payload: boolean }
  | { type: 'SET_CURRENT_MODEL', payload: ChatModel | null }
  | { type: 'SET_CURRENT_CUSTOM_GPT', payload: CustomGPT | null }
  | { type: 'LOAD_AGENTS', payload: Agent[] | null }
  | { type: 'SET_CURRENT_AGENT', payload: Agent | null }
  | { type: 'SET_CHATPANEL_TRANSITIONED', payload: boolean }
  | { type: 'UPDATE_CURRENT_CHAT', payload: Conversation | null }
  | { type: 'UPDATE_CHAT_HISTORY', payload: Conversation }
  | { type: 'SET_CONV_FILES_OPEN', payload: boolean }
  | { type: 'SET_FILE_UPLOAD', payload: any }
  | { type: 'SET_IMAGE_UPLOAD', payload: any }
  | { type: 'SET_INDIVIDUAL_FILE_CHAT', payload: IndividualFileChat[] | null }
  | { type: 'UPDATE_INDIVIDUAL_FILE_CHAT', payload: IndividualFileChat[] }
  | { type: 'SET_FEEDBACK_STATE', payload: { answerId: string; feedback: Feedback.Positive | Feedback.Negative | Feedback.Neutral } }
  | { type: 'SET_SELECTED_CITATION', payload: Citation | null }
  | { type: 'SET_SELECTED_CITATIONS', payload: Citation[] | null }
  | { type: 'SET_SELECTED_CONVERSATION_STARTER', payload: string | null }
  | { type: 'SET_SELECTED_TOOL', payload: string | null }
  | { type: 'LOAD_PERSONALIZATION', payload: Personalization }
  | { type: 'SET_PERSONALIZATION_LOADING', payload: boolean }
  | { type: 'SET_PERSONALIZATION_ERROR', payload: string | Error | null };

export const AppStateContext = createContext<{
  state: AppState;
  dispatch: Dispatch<Action>;
} | undefined>(undefined);
